import {
  DefaultButton,
  IButtonProps,
  Panel,
  Pivot,
  PivotItem,
  PrimaryButton,
  Stack,
  TextField,
} from '@fluentui/react';
import { Input, Label } from '@fluentui/react-components';
import * as fabricImport from 'fabric';
const fabric = (fabricImport as any).fabric || fabricImport;
import React, { useRef, useState } from 'react';
import { useImperativeHandle } from 'react';

export interface IPropsEditorRefType {
  refresh: () => void;
}

export interface IPropsEditorProps {
  isOpen: boolean;
  hidePropEditor: any;
  selectedObj?: fabric.Object;
  onChangeConfirmed: (newValues: Map<string, any>) => void;
}

const disablePivotItemProps: IButtonProps = {
  disabled: true,
  style: { color: 'grey' },
};

const PropsEditor: React.ForwardRefRenderFunction<
  IPropsEditorRefType,
  IPropsEditorProps
> = (props: IPropsEditorProps, forwardedRef) => {
  useImperativeHandle(forwardedRef, () => ({ refresh }));
  const refresh = async () => {};
  const changes = useRef<Map<string, any>>(new Map<string, any>());
  const fonts = [
    'Arial',
    'Tahoma',
    'Georgia',
    'Times New Roman',
    'Courier New',
  ];
  const fontSizes = ['16', '24', '28', '36', '48', '72'];

  // Get initial color value
  const getInitialColor = () => {
    if (!props.selectedObj) return '#000000';
    const fill = props.selectedObj.fill;
    return typeof fill === 'string' ? fill : '#000000';
  };

  const [currentColor, setCurrentColor] = useState(getInitialColor());

  // Update color when selection changes
  React.useEffect(() => {
    if (props.isOpen) {
      setCurrentColor(getInitialColor());
    }
  }, [props.selectedObj, props.isOpen]);

  return (
    <Panel
      isOpen={props.isOpen}
      onDismiss={props.hidePropEditor}
      onOpened={() => changes.current.clear()}
    >
      <Stack
        style={{ padding: 5, height: 500, width: 250 }}
        tokens={{ childrenGap: 10 }}
      >
        <Pivot>
          <PivotItem headerText="Position" key="position">
            <Stack>
              <TextField
                label="Top:"
                defaultValue={props.selectedObj?.top?.toString()}
                onChange={(ev, newValue) => {
                  changes.current.set('top', newValue);
                }}
              />
              <TextField
                label="Left:"
                defaultValue={props.selectedObj?.left?.toString()}
                onChange={(ev, newValue) => {
                  changes.current.set('left', newValue);
                }}
              />
            </Stack>
          </PivotItem>
          <PivotItem
            headerText="Text"
            key="text"
            headerButtonProps={
              !(
                props.selectedObj?.type === 'i-text' ||
                props.selectedObj?.type === 'text'
              )
                ? disablePivotItemProps
                : undefined
            }
          >
            <Stack tokens={{ childrenGap: 10 }}>
              <TextField
                label="Text:"
                defaultValue={
                  props.selectedObj?.type === 'i-text' ||
                  props.selectedObj?.type === 'text'
                    ? (props.selectedObj as fabric.IText).text
                    : ''
                }
                onChange={(ev, newValue) => {
                  changes.current.set('text', newValue);
                }}
              />
              <DefaultButton
                text="Font"
                menuProps={{
                  items: fonts.map((font) => ({
                    key: font,
                    text: font,
                    canCheck: true,
                    isChecked:
                      (props.selectedObj?.type === 'i-text' ||
                        props.selectedObj?.type === 'text') &&
                      (props.selectedObj as fabric.IText).fontFamily === font,
                  })),
                  onItemClick: (ev, item) => {
                    changes.current.set('fontFamily', item?.key);
                  },
                }}
              />
              <DefaultButton
                text="Size"
                menuProps={{
                  items: fontSizes.map((size) => ({
                    key: size,
                    text: size,
                    canCheck: true,
                    isChecked:
                      (props.selectedObj?.type === 'i-text' ||
                        props.selectedObj?.type === 'text') &&
                      (
                        props.selectedObj as fabric.IText
                      ).fontSize?.toString() === size,
                  })),
                  onItemClick: (ev, item) => {
                    changes.current.set('fontSize', item?.key);
                  },
                }}
              />
            </Stack>
          </PivotItem>
          <PivotItem headerText="Color" key="color">
            <Stack>
              <Label>Fill Color</Label>
              <Input
                type="color"
                value={currentColor}
                onChange={(_, data) => {
                  const hexColor = data.value;
                  setCurrentColor(hexColor);
                  // Remove # from hex color for storage
                  changes.current.set('fill', hexColor.replace('#', ''));
                }}
                style={{ width: '100%', height: '40px' }}
              />
              <TextField
                label="Hex Color"
                value={currentColor}
                onChange={(_, newValue) => {
                  const value = newValue || '';
                  const hexValue = value.startsWith('#') ? value : `#${value}`;
                  setCurrentColor(hexValue);
                  changes.current.set('fill', value.replace('#', ''));
                }}
                placeholder="#000000"
              />
            </Stack>
          </PivotItem>
        </Pivot>
        <PrimaryButton
          onClick={() => {
            props.hidePropEditor();
            props.onChangeConfirmed(changes.current);
          }}
        >
          ok
        </PrimaryButton>
      </Stack>
    </Panel>
  );
};

export default React.forwardRef(PropsEditor);
